import { useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { business } from '../data/siteData'
import './BookingForm.css'

const eventTypes = [
  'Corporate Event',
  'Product Launch',
  'Private Party',
  'Wedding',
  'Stage Decoration',
  'Award & Cultural Show',
  'Other',
]

const initialForm = {
  name: '',
  phone: '',
  email: '',
  eventType: eventTypes[0],
  date: '',
  guests: '',
  location: '',
  budget: '',
  message: '',
}

export default function BookingForm() {
  const [form, setForm] = useState(initialForm)
  const [errors, setErrors] = useState({})
  const [status, setStatus] = useState('idle')
  const [serverError, setServerError] = useState('')
  const [captchaToken, setCaptchaToken] = useState(null)
  const captchaRef = useRef(null)

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))

  const validate = () => {
    const e = {}
    if (!form.name.trim()) e.name = 'Please enter your name'
    if (!/^[+\d][\d\s-]{7,}$/.test(form.phone.trim())) e.phone = 'Enter a valid phone number'
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) e.email = 'Enter a valid email'
    if (!form.date) e.date = 'Please choose a date'
    if (!captchaToken) e.captcha = 'Please verify you are not a robot'
    return e
  }

  const handleSubmit = async (evt) => {
    evt.preventDefault()
    const validationErrors = validate()
    setErrors(validationErrors)
    if (Object.keys(validationErrors).length > 0) return

    setStatus('sending')
    setServerError('')

    try {
      const res = await fetch(process.env.REACT_APP_PHP_MAIL_SENDER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, captchaToken, formType: 'aselite-booking' }),
      })
      const result = await res.json()

      if (result.success) {
        setStatus('sent')
      } else {
        setStatus('error')
        setServerError(result.message || 'Something went wrong. Please try again.')
      }
    } catch (err) {
      setStatus('error')
      setServerError('Could not reach the server. Please check your connection and try again.')
    } finally {
      captchaRef.current?.reset()
      setCaptchaToken(null)
    }
  }

  if (status === 'sent') {
    return (
      <div className="booking-success">
        <span className="booking-success-icon">✓</span>
        <h3>Your enquiry has been sent</h3>
        <p>
          Thanks, {form.name}! We've emailed you a confirmation, and our team will get back to
          you within one business day. You can also call us directly at{' '}
          <a href={business.phoneHref}>{business.phone}</a>.
        </p>
        <button className="btn btn-ghost" onClick={() => { setForm(initialForm); setErrors({}); setStatus('idle'); setServerError('') }}>
          Submit Another Enquiry
        </button>
      </div>
    )
  }

  return (
    <form className="booking-form" onSubmit={handleSubmit} noValidate>
      <div className="form-row">
        <div className="form-field">
          <label htmlFor="name">Full Name *</label>
          <input id="name" name="name" type="text" value={form.name} onChange={update('name')} placeholder="Your name" autoComplete="name" />
          {errors.name && <span className="form-error">{errors.name}</span>}
        </div>
        <div className="form-field">
          <label htmlFor="phone">Phone Number *</label>
          <input id="phone" name="phone" type="tel" value={form.phone} onChange={update('phone')} placeholder="+91 XXXXX XXXXX" autoComplete="tel"/>
          {errors.phone && <span className="form-error">{errors.phone}</span>}
        </div>
      </div>

      <div className="form-row">
        <div className="form-field">
          <label htmlFor="email">Email Address</label>
          <input id="email" name="email" type="email" value={form.email} onChange={update('email')} placeholder="you@example.com" autoComplete="email" />
          {errors.email && <span className="form-error">{errors.email}</span>}
        </div>
        <div className="form-field">
          <label htmlFor="eventType">Event Type *</label>
          <select id="eventType" name="eventType" value={form.eventType} onChange={update('eventType')}>
            {eventTypes.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-row form-row-3">
        <div className="form-field">
          <label htmlFor="date">Event Date *</label>
          <input id="date" name="date" type="date" value={form.date} onChange={update('date')} autoComplete="off"/>
          {errors.date && <span className="form-error">{errors.date}</span>}
        </div>
        <div className="form-field">
          <label htmlFor="guests">Estimated Guests</label>
          <input id="guests" name="guests" type="number" min="1" value={form.guests} onChange={update('guests')} placeholder="e.g. 150" autoComplete="off"/>
        </div>
        <div className="form-field">
          <label htmlFor="budget">Budget Range</label>
          <select id="budget" name="budget" value={form.budget} onChange={update('budget')} autoComplete="off">
            <option value="">Select a range</option>
            <option>Under ₹50,000</option>
            <option>₹50,000 – ₹1,50,000</option>
            <option>₹1,50,000 – ₹3,00,000</option>
            <option>₹3,00,000+</option>
            <option>Not sure yet</option>
          </select>
        </div>
      </div>

      <div className="form-field">
        <label htmlFor="location">Venue / Location</label>
        <input id="location" name="location" type="text" value={form.location} onChange={update('location')} placeholder="Venue name or area" autoComplete="address-level2"/>
      </div>

      <div className="form-field">
        <label htmlFor="message">Tell Us About Your Event</label>
        <textarea id="message" name="message" rows="4" value={form.message} onChange={update('message')} placeholder="Theme, must-haves, anything we should know..."  autoComplete="off" />
      </div>

      <div className="form-field form-field-captcha">
        <ReCAPTCHA
          ref={captchaRef}
          sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
          theme="dark"
          onChange={(token) => setCaptchaToken(token)}
          onExpired={() => setCaptchaToken(null)}
        />
        {errors.captcha && <span className="form-error">{errors.captcha}</span>}
      </div>

      {status === 'error' && <p className="form-error">{serverError}</p>}

      <button type="submit" className="btn btn-primary booking-submit" disabled={status === 'sending'}>
        {status === 'sending' ? 'Sending…' : 'Send Booking Enquiry'}
      </button>
      <p className="booking-note">
        We'll email you straight away, and send the details to our team at {business.email}.
        Prefer to talk it through? Call {business.phone}.
      </p>
    </form>
  )
}
